<template>
  <div class="w-full border rounded-lg">
    <!-- Selected customer display -->
    <div v-if="_value && !isMulti" class="p-1 flex items-center justify-between">
      <CustomerElementsItem :customer="_value" class="px-1" />

      <div class="flex items-center ml-2 space-x-1">
        <UTooltip
          v-if="!_value.isNewRecord && !isCustomerPanelOpen && $grants.all({ customer: ['profile'] })"
          :text="$t('components.customer.elements.select.openCustomerProfile')"
        >
          <UButton
            variant="subtle"
            size="sm"
            icon="lucide:user"
            @click="handleOpenCustomerProfile"
          />
        </UTooltip>

        <UButton
          v-if="isExistingCustomer && $grants.all({ customer: ['profile'] })"
          variant="subtle"
          size="sm"
          icon="lucide:chevron-down"
          :disabled="isSyncing"
          @click="handleCustomerPreviewToggle()"
        />

        <UButton
          variant="subtle"
          size="sm"
          icon="lucide:x"
          @click="() => { _value = null }"
        />
      </div>
    </div>

    <!-- Search input -->
    <UInputMenu
      v-else
      v-model="_value"
      v-model:search-term="searchTerm"
      label-key="displayName"
      ignore-filter
      :placeholder="placeholder || (isMulti ? $t('customer.add') : $t('components.customer.elements.select.startTyping'))"
      :multiple="isMulti || undefined"
      :create-item="create ? { position: 'top', when: 'always' } : undefined"
      :loading="isSyncing || searching"
      :by="compareCustomers"
      class="w-full"
      v-bind="$attrs"
      :items="searchResults"
      size="lg"
      variant="none"
      @create="handleCustomerCreateClick"
    >


      <template #item="{ item }">
        <CustomerElementsItem :customer="item" />
      </template>

      <template v-if="create" #create-item-label="{ item }">
        <span class="text-blue-600 font-medium dark:text-blue-500">{{ $t('customer.new') }}</span>
        {{ item ? ` - ${item}` : '' }}
      </template>
    </UInputMenu>

    <o-collapse v-if="isExistingCustomer" ref="customerPreviewCollapse" no-header>
      <div class="w-full bg-gray-50 border dark:bg-gray-800/50 dark:border-gray-800 rounded p-3 mt-2">
        <customer-preview :customer="_value" />
      </div>
    </o-collapse>

    <div v-if="isNewCustomer" class="w-full bg-gray-50 border dark:bg-gray-800/50 dark:border-gray-800 rounded p-3 mt-2">
      <customer-create v-model:customer="_value" />
    </div>
  </div>
</template>

<script setup lang="ts">
import Fuse from 'fuse.js'
import debounce from 'lodash/debounce'

import CustomerPreview from './Preview.vue'
import CustomerCreate from './Create.vue'

import { Customer, CustomerPrivacyConsent } from '~/_api/models/customer'
import type { ApiResponse } from '~/_api/lib/response'

import { useCustomerStore } from '~/stores/useCustomerStore'
import { usePanelStore } from '~/stores/usePanelStore'

interface Props {
  modelValue?: any
  preview?: boolean
  create?: boolean
  filter?: Function | null
  placeholder?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  preview: false,
  create: false,
  filter: null,
  placeholder: null,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void
}>()

const { $api, $panel } = useNuxtApp()

const customerStore = useCustomerStore()
const panelStore = usePanelStore()

const customerPreviewCollapse = ref<any>()
const searchTerm = ref('')
const defaults = ref<any>(null)
const searchResults = ref<any[]>([])
const searching = ref(false)

function toArray<T = any>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : []
}

const fuseKeys = [
  { name: 'displayName', weight: 2 },
  'email',
  'phone',
  'address',
  'city',
  'postcode',
  'suburb',
  'region',
  'homePhone',
  'company',
]

const _value = computed({
  get () {
    return props.modelValue
  },
  set (value) {
    emit('update:modelValue', value)
  },
})

const customers = computed(() => toArray(customerStore.all))
const isSyncing = computed(() => customerStore.isSyncing)

const filteredCustomers = computed(() => {
  let list = [...toArray(customers.value)]
  if (props.filter) {
    list = list.filter(props.filter as any)
  }
  return list
})

const search = debounce((q: string) => {
  if (!q) {
    searchResults.value = []
    searching.value = false
    return
  }

  const tokens = q.trim().split(/\s+/).filter(Boolean)
  const fuse = new Fuse(filteredCustomers.value, {
    keys: fuseKeys,
    findAllMatches: false,
    threshold: 0.3,
    useExtendedSearch: true,
  })

  const pattern = tokens.length > 1
    ? { $and: tokens.map(token => ({ displayName: `'${token}` })) }
    : q

  searchResults.value = fuse
    .search(pattern, { limit: 30 })
    .map(({ item }) => item)

  searching.value = false
}, 300)

watch(searchTerm, (val) => {
  if (val) {
    searching.value = true
  }
  search(val)
})

const isExistingCustomer = computed(() => {
  return props.preview && _value.value && !_value.value.isNewRecord
})

const isNewCustomer = computed(() => {
  return props.create && _value.value && _value.value.isNewRecord
})

const isCustomerPanelOpen = computed(() => {
  return panelStore.activePanels?.some((p: any) => typeof p.key === 'string' && p.key.startsWith('customer/'))
})

const isMulti = computed(() => {
  return props.modelValue && Array.isArray(props.modelValue)
})

const compareCustomers = (a: any, b: any) => {
  if (!a || !b) return false
  if (a.idCustomer && b.idCustomer) return Number(a.idCustomer) === Number(b.idCustomer)
  return a === b
}

// Load customer defaults for creating new customers
$api.customer.defaults()
  .then((response: ApiResponse<Customer>) => {
    defaults.value = response.data || {}
  })

onMounted(() => {
  customerStore.sync()
})

const handleCustomerCreateClick = (term?: string) => {
  const defs = { ...defaults.value }
  const customer = new Customer(defs)
  const q = term || searchTerm.value || ''
  const [firstName = '', lastName = ''] = q.split(/\s+(.*)/)

  customer.firstName = firstName
  customer.lastName = lastName
  customer.consentAccepted = CustomerPrivacyConsent.notObtained

  searchTerm.value = ''
  _value.value = customer
}

const handleCustomerPreviewToggle = () => {
  customerPreviewCollapse.value?.toggle()
}

const handleOpenCustomerProfile = () => {
  if (!_value.value?.idCustomer) return
  ;($panel as any).open('customer/panel', { id: String(_value.value.idCustomer) })
}
</script>
